// The one place Discover decides how a row describes a place. The gems drawer,
// the coupon/raffle rows and the cluster modal all render what this returns —
// they previously each built the lines themselves, which is why they disagreed.
import { isPlaceholderValue, normalizeLabelText } from "./businessLocationLabel";

const SEPARATOR = " · ";
const DEFAULT_TITLE = "Time capsule";

export const MEMBER_EXCLUSIVE_TEASER_TITLE =
  "Only Insiders can open this one — joining is free";

// Discover's own bury wizard prefills the message body with this phrase followed
// by the place and/or address. It reaches the title slot via normalizeNearbyGem,
// which falls back to messageBody. The wizard imports this constant so the
// producer and the detector cannot drift apart.
export const FRIEND_CAPSULE_TITLE_PREFIX = "This place made me think of you:";

function isFriendBoilerplateTitle(value) {
  const candidate = normalizeLabelText(value);
  if (!candidate) return false;
  return candidate.startsWith(normalizeLabelText(FRIEND_CAPSULE_TITLE_PREFIX));
}

function str(value) {
  return String(value || "").trim();
}

function sameText(a, b) {
  return Boolean(a) && normalizeLabelText(a) === normalizeLabelText(b);
}

// Keyed on capsuleType, which gems, offers and markers all carry — a single
// predicate for every surface. Gating this per-surface is what let the cluster
// modal ship without it.
export function isGatedMemberExclusive(source = {}, { isInsider = false } = {}) {
  const type = normalizeLabelText(source?.capsuleType).replace(/[-_\s]/g, "");
  return type === "memberexclusive" && !isInsider;
}

export function buildDiscoveryRowLabels(
  source = {},
  { isInsider = false, identityInHeader = false } = {}
) {
  const companyName = str(source?.companyName);
  const senderLabel = str(source?.senderLabel);
  const locationName = str(source?.locationName);
  const address = str(source?.address);

  // Studio auto-fills a blank subject from the place name, the business name or
  // the address, so a title matching any of those is not a real message title.
  const autoFilled = [address, locationName, companyName, str(source?.placeName)];

  const title = isGatedMemberExclusive(source, { isInsider })
    ? MEMBER_EXCLUSIVE_TEASER_TITLE
    : [source?.messageTitle, source?.title, source?.headline]
        .map(str)
        .find(
          (value) =>
            value && !isPlaceholderValue(value, autoFilled) && !isFriendBoilerplateTitle(value)
        ) ||
      str(source?.seedName) ||
      locationName ||
      address ||
      DEFAULT_TITLE;

  // Compare the place against the identity we resolved, not the one we may be
  // about to hide — otherwise hiding it would let a duplicate place reappear.
  const identityRaw = companyName || senderLabel;
  const identity = identityInHeader || sameText(identityRaw, title) ? "" : identityRaw;

  const place =
    locationName && !sameText(locationName, identityRaw) && !sameText(locationName, title)
      ? locationName
      : "";

  const addressLine =
    address && !sameText(address, place) && !sameText(address, title) && !sameText(address, identityRaw)
      ? address
      : "";

  const secondary = [identity, place].filter(Boolean).join(SEPARATOR);
  // With no business and no named place, the address is the only thing left to
  // say — it moves up rather than leaving a gap.
  if (!secondary) return { title, secondary: addressLine, tertiary: "" };

  return { title, secondary, tertiary: addressLine };
}
